import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import dayjs from "@calcom/dayjs";

import type { CalendarEvent } from "../types/events";

export type DragMode = "move" | "resize";

export type DragPreview = {
  eventId: number;
  mode: DragMode;
  /** Snapped offsets applied to the chip while dragging, so the preview matches what will be saved. */
  minutes: number;
  days: number;
  /** Width of one day column, in px — a day of travel is a column, not a chip width. */
  columnWidth: number;
};

/** Pointer travel before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD_PX = 4;

type Session = {
  event: CalendarEvent;
  mode: DragMode;
  startX: number;
  startY: number;
  pixelsPerMinute: number;
  columnWidth: number;
  snapMinutes: number;
  durationMinutes: number;
  moved: boolean;
};

/**
 * Drag-to-move and drag-to-resize for the week grid, on plain pointer events — the workspace has no
 * drag-and-drop library, and the grid already exposes everything needed: `--one-minute-height` for
 * the vertical scale and the day column's width for the horizontal one.
 *
 * The hook never mutates the events; it reports the snapped result and lets the consumer decide
 * (Helima confirms the change in a dialog before saving).
 */
export function useEventDrag({
  gridCellsPerHour,
  isEventEditable,
  onEventDrop,
  onEventResize,
}: {
  gridCellsPerHour: number;
  isEventEditable?: (event: CalendarEvent) => boolean;
  onEventDrop?: (event: CalendarEvent, change: { start: Date; end: Date }) => void;
  onEventResize?: (event: CalendarEvent, change: { start: Date; end: Date }) => void;
}) {
  const [preview, setPreview] = useState<DragPreview | null>(null);
  // Mirrored in a ref: pointerup must read the latest offsets synchronously. Doing that work inside
  // a functional setState would run it twice under StrictMode — i.e. two save calls per drag.
  const previewRef = useRef<DragPreview | null>(null);
  const session = useRef<Session | null>(null);
  // Read by the click handler to swallow the click that ends a drag.
  const justDragged = useRef(false);

  const enabled = Boolean(onEventDrop || onEventResize);

  const beginDrag = useCallback(
    (nativeEvent: ReactPointerEvent<HTMLElement>, event: CalendarEvent, mode: DragMode) => {
      if (!enabled) return;
      if (mode === "move" && !onEventDrop) return;
      if (mode === "resize" && !onEventResize) return;
      if (isEventEditable && !isEventEditable(event)) return;
      // Only the primary button drags; a right-click must stay a context menu.
      if (nativeEvent.button !== 0) return;

      const chip = nativeEvent.currentTarget.closest("[data-calendar-event-id]") as HTMLElement | null;
      const column = chip?.parentElement as HTMLElement | null;
      if (!chip || !column) return;

      const oneMinute = parseFloat(
        getComputedStyle(chip).getPropertyValue("--one-minute-height") || ""
      );
      const pixelsPerMinute = Number.isFinite(oneMinute) && oneMinute > 0 ? oneMinute : 58 / 60;
      const snapMinutes = Math.max(1, Math.round(60 / (gridCellsPerHour || 4)));

      session.current = {
        event,
        mode,
        startX: nativeEvent.clientX,
        startY: nativeEvent.clientY,
        pixelsPerMinute,
        columnWidth: column.getBoundingClientRect().width || 1,
        snapMinutes,
        durationMinutes: Math.max(1, dayjs(event.end).diff(dayjs(event.start), "minute")),
        moved: false,
      };
    },
    [enabled, gridCellsPerHour, isEventEditable, onEventDrop, onEventResize]
  );

  useEffect(() => {
    if (!enabled) return;

    const onPointerMove = (e: PointerEvent) => {
      const s = session.current;
      if (!s) return;

      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.moved && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      s.moved = true;
      // Stops the browser from selecting text across the grid mid-drag.
      e.preventDefault();

      const rawMinutes = dy / s.pixelsPerMinute;
      let minutes = Math.round(rawMinutes / s.snapMinutes) * s.snapMinutes;
      // A resize may not shrink the event below one grid step.
      if (s.mode === "resize") {
        minutes = Math.max(minutes, s.snapMinutes - s.durationMinutes);
      }
      const days = s.mode === "move" ? Math.round(dx / s.columnWidth) : 0;

      const next = { eventId: s.event.id, mode: s.mode, minutes, days, columnWidth: s.columnWidth };
      previewRef.current = next;
      setPreview(next);
    };

    const onPointerUp = () => {
      const s = session.current;
      const current = previewRef.current;
      session.current = null;
      previewRef.current = null;
      setPreview(null);
      if (!s || !s.moved || !current) return;
      if (current.minutes === 0 && current.days === 0) return;

      justDragged.current = true;
      // Cleared on the next tick, after the click this pointerup produces has been swallowed.
      setTimeout(() => {
        justDragged.current = false;
      }, 0);

      const start = dayjs(s.event.start);
      if (s.mode === "move") {
        const newStart = start.add(current.days, "day").add(current.minutes, "minute");
        onEventDrop?.(s.event, {
          start: newStart.toDate(),
          end: newStart.add(s.durationMinutes, "minute").toDate(),
        });
      } else {
        onEventResize?.(s.event, {
          start: start.toDate(),
          end: dayjs(s.event.end).add(current.minutes, "minute").toDate(),
        });
      }
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [enabled, onEventDrop, onEventResize]);

  /** Swallows the click that a finished drag emits, so it doesn't also open the event. */
  const swallowClickAfterDrag = useCallback((e: ReactMouseEvent) => {
    if (!justDragged.current) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return { preview, beginDrag, swallowClickAfterDrag, isDragEnabled: enabled };
}
